import { useNavigate } from "@reach/router";
import { FormEvent, FunctionComponent, useContext, useState } from "react";
import BackButton from "../../components/back-button/BackButton";
import Button from "../../components/button/Button";
import FileUploader from "../../components/file-uploader/FileUploader";
import Input, { InputValidation } from "../../components/input/Input";
import Select from "../../components/select/Select";
import ToolTip from "../../components/ToolTip/ToolTip";
import LayoutContext from "../../context/layout-context";
import { certificateConfigTypesOptions } from "../../utils/constants";
import { uploadCertificate } from "../../utils/helpers/data/certificate-upload";
import { CertificateUploadData } from "../../utils/helpers/types";
import { PartialLoose } from "../../utils/helpers/types-helpers";
import { alphaNumericValidation } from "../../utils/helpers/validation-helpers";
import { resources } from "../../utils/resources";

const initialFormData: CertificateUploadData = {
  configName: "",
  configType: "",
  ksPassword: "",
  file: null
};

const validations: PartialLoose<CertificateUploadData, InputValidation> = {
  configName: {
    maxLength: 32,
    transform: alphaNumericValidation,
    required: true
  },
  ksPassword: {
    maxLength: 64
  }
};

const CertificateUploadPage: FunctionComponent = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const { notify } = useContext(LayoutContext);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!formData.configType) {
      notify("error", "Please select a config type to continue");
      return;
    }
    if (!formData.file) {
      notify("error", "Please upload a certificate to continue");
      return;
    }
    setLoading(true);
    const response = await uploadCertificate(formData);
    setLoading(false);
    if (response.error) {
      notify("error", response.message as string);
      return;
    }
    notify("success", "Certificate uploaded successfully");
    setFormData(initialFormData);
  };

  const handleChange = (key: keyof CertificateUploadData, value: any) => {
    let extraProps: Partial<CertificateUploadData> = {};
    if (key === "file") {
      const ext = value ? `.${value?.name?.split(".").pop() || ""}` : "";
      const option = ext
        ? certificateConfigTypesOptions.find(_option =>
            String(_option.value).includes(ext)
          )
        : null;
      const configType = String(option?.value || "");
      extraProps = { configType };
      console.log({ configType, existing: formData.configType });
      if (
        formData.configType &&
        configType &&
        formData.configType !== configType
      ) {
        notify(
          "info",
          `Different config type (${configType}) has been detected and automatically selected`
        );
      }
    }
    setFormData({ ...formData, [key]: value, ...extraProps });
  };

  return (
    <main className="content-layout">
      <BackButton
        round
        text="Back to Configuration"
        className="vertical-margin"
      />
      <br />
      <h1 className="title thin vertical-margin spaced">Certificate Upload</h1>
      <form
        onSubmit={handleSave}
        className="generic-form certificate-upload-form"
      >
        <div className="input-group">
          <span className="question">
            <span className="required">Config name</span>
            <ToolTip
              toolTip="The name of the configuration certificate"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.configName}
            onChange={value => handleChange("configName", value)}
            placeholder="Enter config name"
            inputValidation={validations.configName}
            id="certConfigName"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Config Type</span>
            <ToolTip
              toolTip="File type for uploaded certificate"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Select
            options={certificateConfigTypesOptions}
            value={formData.configType}
            onSelect={value => handleChange("configType", value)}
            placeholder="Select config type"
            theme="light"
            responsive
            id="certConfigType"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span className="required">Certificate file</span>
          </span>
          <FileUploader
            acceptedTypes={[".pfx", ".ks", ".pem"]}
            titleText="Select file from device"
            subtitle="Or drag and drop a file"
            onChange={file => handleChange("file", file)}
            file={formData.file}
            id="certFile"
          />
        </div>

        <div className="input-group">
          <span className="question">
            <span>Keystore Password</span>
            <ToolTip
              toolTip="The keystore password, this is required for client authentication certificates and private keystore"
              anchor={
                <img
                  alt="info tooltip"
                  src={resources.icons.INFO_CIRCLE_SOLID}
                  className="generic-icon small"
                />
              }
            />
          </span>
          <Input
            value={formData.ksPassword}
            onChange={value => handleChange("ksPassword", value)}
            placeholder="Enter password"
            id="certKSPassword"
          />
        </div>

        <br />
        <br />
        <div className="flex spaced end vertical-margin">
          <Button
            type="accent"
            buttonType="button"
            onClick={() => navigate(-1)}
            id="certUploadCancel"
          >
            Cancel
          </Button>
          <Button buttonType="submit" loading={loading} id="certUploadSubmit">
            Upload Certificate
          </Button>
        </div>
      </form>
    </main>
  );
};

export default CertificateUploadPage;
